// Section 24.12 store actions: named slots, loading autosaves, export and import, and the
// "played on an older balance" choice. Loading is allowed whenever a season is not resolving.
import { isSimError } from '../../sim/api';
import {
  deleteSave,
  exportFile,
  forkOntoCurrent,
  importText,
  loadEnvelope,
  makeEnvelope,
  readSave,
  writeSlot,
  type ExportFile,
  type SaveMeta,
} from '../../persist';
import { withBoundary } from '../boundary';
import { requireGame, requireStatus } from '../guards';
import { currentRun, runConfig } from '../run';
import { enterLoaded, refreshSaves } from '../session';
import { initialUi, patchUi, saves, settings } from '../state';
import { currentStorage } from '../storage';
import { stopObserver } from './observer-timer';

const LOADABLE = ['title', 'deciding', 'reporting', 'paused', 'ended'] as const;

function envelopeNow(opts: { withSnapshot?: boolean; withLedger?: boolean } = {}) {
  const cfg = runConfig();
  return makeEnvelope(requireGame(), new Date().toISOString(), {
    ...opts,
    ...(cfg === undefined ? {} : { cfg }),
  });
}

/** Save to a named slot (not in ironman: one rolling save only, Section 3). */
export const saveToSlot = withBoundary((name: string): boolean => {
  requireStatus('deciding', 'reporting', 'ended');
  const state = requireGame();
  if (state.options.ironman) {
    patchUi({ notice: { code: 'ironman-no-slots', detail: null } });
    return false;
  }
  const storage = currentStorage();
  const label = name.trim() === '' ? `Year ${state.year} ${state.season}` : name.trim();
  if (storage === null) {
    patchUi({ notice: { code: 'storage-unavailable', detail: null } });
    return false;
  }
  const result = writeSlot(storage, label, envelopeNow(), currentRun().runId);
  if (!result.ok) {
    patchUi({ notice: { code: 'save-failed', detail: label } });
    return false;
  }
  if (result.shrunkTo !== null) settings.value = { ...settings.value, autosaveCount: result.shrunkTo };
  patchUi({ notice: { code: result.shrunkTo === null ? 'saved' : 'quota', detail: label } });
  refreshSaves();
  return true;
});

function findSave(kind: SaveMeta['kind'], id: string): SaveMeta | undefined {
  const list = kind === 'slot' ? saves.value.slots : saves.value.autosaves;
  return list.find((m) => m.kind === kind && m.id === id);
}

function loadMeta(meta: SaveMeta | undefined): boolean {
  requireStatus(...LOADABLE);
  const storage = currentStorage();
  const envelope = meta === undefined || storage === null ? null : readSave(storage, meta);
  if (meta === undefined || envelope === null) {
    patchUi({ notice: { code: 'load-failed', detail: meta?.name ?? null } });
    refreshSaves();
    return false;
  }
  stopObserver();
  patchUi({ ...initialUi, notice: { code: 'loaded', detail: meta.name } });
  enterLoaded(loadEnvelope(envelope), meta.runId);
  refreshSaves();
  return true;
}

export const loadSlot = withBoundary((id: string): boolean => loadMeta(findSave('slot', id)));

/**
 * Load an earlier autosave: by index into the ring (0 = newest, the start-of-run save last) or
 * by id. Ironman runs keep only their newest autosave, so the ring never offers an earlier one
 * (Section 3).
 */
export const loadAutosave = withBoundary((which: number | string): boolean => {
  const meta =
    typeof which === 'number'
      ? [
          ...saves.value.autosaves.filter((m) => m.kind === 'autosave'),
          ...saves.value.autosaves.filter((m) => m.kind === 'start'),
        ][which]
      : (findSave('autosave', which) ?? findSave('start', which));
  return loadMeta(meta);
});

export const deleteSlot = withBoundary((id: string): void => {
  const storage = currentStorage();
  if (storage !== null) deleteSave(storage, { kind: 'slot', id });
  refreshSaves();
});

/**
 * Export the run as a JSON file: the full save, or its shareable form (the envelope minus the
 * snapshot, Section 21). The UI turns the result into a download.
 */
export const exportRun = withBoundary((opts: { shareable?: boolean } = {}): ExportFile => {
  requireStatus('deciding', 'reporting', 'ended', 'paused');
  const shareable = opts.shareable === true;
  const file = exportFile(envelopeNow({ withSnapshot: !shareable, withLedger: true }));
  patchUi({ notice: { code: 'exported', detail: file.filename } });
  return file;
});

/** Import from a file's text: validated (and migrated) before the store is touched. */
export const importRunText = withBoundary((text: string): boolean => {
  requireStatus(...LOADABLE);
  let loaded;
  try {
    loaded = importText(text);
  } catch (error) {
    if (!isSimError(error)) throw error;
    patchUi({ notice: { code: 'import-failed', detail: error.message } });
    return false;
  }
  stopObserver();
  patchUi({ ...initialUi, notice: { code: 'imported', detail: loaded.envelope.seed } });
  enterLoaded(loaded, null);
  refreshSaves();
  return true;
});

/** Import a run from a File (or any Blob) chosen by the player. */
export async function importRun(file: Blob | string): Promise<boolean> {
  const text = typeof file === 'string' ? file : await file.text();
  return importRunText(text) ?? false;
}

/** Keep playing on the saved (older) balance: just acknowledge the note. */
export const continueOnSavedConfig = (): void => {
  patchUi({ olderBalance: null });
};

/** Replay the run's decisions on the current balance and continue from there (24.12). */
export const forkOntoCurrentConfig = withBoundary((): void => {
  requireStatus(...LOADABLE);
  const envelope = currentRun().envelope;
  if (envelope === null) {
    patchUi({ olderBalance: null });
    return;
  }
  const forked = forkOntoCurrent(envelope);
  patchUi({ ...initialUi, notice: { code: 'forked', detail: null } });
  enterLoaded(forked, null);
  refreshSaves();
});
